<template>
  <div class="orders-tab">
    <h2>Orders</h2>

    <Filters v-model="orderFilters" :options="filterOptions" @reset="handleFiltersReset" />

    <div v-if="searchLoading" class="orders-tab__loading">
      <Spinner />
    </div>
<!--    TODO: Ta bort när vi har resultattabellen på plats-->
    <p v-else>
      {{ orders.length }} resultat
    </p>
  </div>
</template>

<script setup lang="ts">
import Filters from "@components/Filters/Filters.vue";
import { parseTripType } from "@features/Pages/SalesAdminPage/Services/trip-type-options";
import { TripTypeFilter } from "@features/Pages/SalesAdminPage/Types/TripTypeFilter";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import Spinner from "@shared/Components/Spinner/Spinner.vue";
import { useApi } from "@shared/Services/axios-service";
import { SalesAdminSettingsResponse } from "@features/Pages/SalesAdminPage/Types/SalesAdminSettingsResponse";
import { SearchTripsRequest } from "@features/Pages/SalesAdminPage/Types/SearchTripsRequest";
import { SearchTripsResponse } from "@features/Pages/SalesAdminPage/Types/SearchTripsResponse";
import { FiltersOptions } from "@components/Filters/FiltersOptions";
import { useToast } from "@components/Toast/toast-service";
import { ToastTypes } from "@components/Toast/ToastTypes";

const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_PAGE_SIZE = 20;

const orderFilters = ref<FiltersModel>({
  text: "",
  dateTime: "",
  location: null,
  company: null,
  tripType: null,
  showPassed: false,
});

const orders = ref<OrderDetailsResponse[]>([]);
const filterOptions = ref<FiltersOptions>({
  locations: [],
  companies: [],
  tripTypes: [],
});
const { addToast } = useToast();
const settingsApi = useApi<SalesAdminSettingsResponse>("/salesadmin/getsettings");
const searchApi = useApi<SearchTripsResponse>("/salesadmin/searchtrips");
const searchLoading = computed(() => searchApi.loading.value || settingsApi.loading.value);

let debounceTimer: number | null = null;
let latestSearchToken = 0;
let skipNextFilterWatchSearch = false;

watch(
  orderFilters,
  () => {
    if (skipNextFilterWatchSearch) {
      skipNextFilterWatchSearch = false;
      return;
    }

    queueSearch();
  },
  { deep: true },
);

onMounted(async () => {
  await loadFilterOptions();
  await runSearch();
});

onBeforeUnmount(() => {
  clearPendingSearch();
});

function queueSearch(): void {
  clearPendingSearch();
  debounceTimer = window.setTimeout(() => {
    void runSearch();
  }, SEARCH_DEBOUNCE_MS);
}

function clearPendingSearch(): void {
  if (debounceTimer !== null) {
    window.clearTimeout(debounceTimer);
    debounceTimer = null;
  }
}

async function loadFilterOptions(): Promise<void> {
  try {
    const settings = await settingsApi.get<SalesAdminSettingsResponse>();
    filterOptions.value = {
      locations: settings.locations
        .filter(isNonEmptyString)
        .filter((value, index, allValues) => allValues.indexOf(value) === index)
        .sort((left, right) => left.localeCompare(right, "sv-SE")),
      companies: settings.companies
        .filter(isNonEmptyString)
        .filter((value, index, allValues) => allValues.indexOf(value) === index)
        .sort((left, right) => left.localeCompare(right, "sv-SE")),
      tripTypes: settings.tripTypes,
    };
  } catch (error) {
    addToast("Kunde inte ladda in filteralternativ.", ToastTypes.Error);
  }
}

async function runSearch(): Promise<void> {
  const searchToken = ++latestSearchToken;

  try {
    const response = await searchApi.post<SearchTripsResponse, SearchTripsRequest>(createSearchRequest());
    if (searchToken !== latestSearchToken) {
      return;
    }
    
    orders.value = response.items;
    
  } catch (error) {
    if (searchToken !== latestSearchToken) {
      return;
    }

    orders.value = [];
    addToast("Kunde inte hämta resor.", ToastTypes.Error);
  }
}

async function handleFiltersReset(): Promise<void> {
  skipNextFilterWatchSearch = true;
  clearPendingSearch();
  await nextTick();
  await runSearch();
}

function createSearchRequest(): SearchTripsRequest {
  return {
    text: normalizeString(orderFilters.value.text),
    location: orderFilters.value.location,
    exactTravelDate: normalizeString(orderFilters.value.dateTime) ?? undefined,
    showPassed: orderFilters.value.showPassed,
    company: orderFilters.value.company,
    tripType: mapTripType(orderFilters.value.tripType),
    currentPageNumber: 1,
    pageSize: SEARCH_PAGE_SIZE,
  };
}

function normalizeString(value: string | null | undefined): string | null {
  const trimmedValue = value?.trim() ?? "";
  return trimmedValue === "" ? null : trimmedValue;
}

function isNonEmptyString(value: string | null | undefined): value is string {
  return (value?.trim() ?? "") !== "";
}

function mapTripType(value: string | null): TripTypeFilter | null {
  return parseTripType(value);
}
</script>
